import { vocabularyCategorySchema, type VocabularyCategory } from '@sl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { vocabularyApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

export function VocabularyPage(): JSX.Element {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [term, setTerm] = useState('');
  const [category, setCategory] = useState<VocabularyCategory>('CLIENT');
  const [aliases, setAliases] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['vocabulary'], queryFn: vocabularyApi.list });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
  };

  const create = useMutation({
    mutationFn: () =>
      vocabularyApi.create({
        term: term.trim(),
        category,
        aliases: aliases
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setTerm('');
      setAliases('');
      setError(null);
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => vocabularyApi.remove(id),
    onSuccess: invalidate,
  });

  return (
    <>
      <h1>{t('vocabulary.title')}</h1>
      <p className="muted">{t('vocabulary.subtitle')}</p>
      {!isAdmin && <div className="banner">{t('vocabulary.adminOnly')}</div>}
      {error && <div className="banner error">{error}</div>}

      {isAdmin && (
        <form
          className="card"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="row">
            <div className="field">
              <label htmlFor="term">{t('vocabulary.term')}</label>
              <input id="term" required value={term} onChange={(e) => setTerm(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="category">{t('vocabulary.category')}</label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value as VocabularyCategory)}
              >
                {vocabularyCategorySchema.options.map((o) => (
                  <option key={o} value={o}>
                    {t(`categories.${o}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="aliases">{t('vocabulary.aliases')}</label>
              <input id="aliases" value={aliases} onChange={(e) => setAliases(e.target.value)} />
            </div>
          </div>
          <button type="submit" className="gold" disabled={create.isPending || !term.trim()}>
            {t('vocabulary.add')}
          </button>
        </form>
      )}

      {isLoading && <p className="muted">{t('common.loading')}</p>}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>{t('vocabulary.term')}</th>
              <th>{t('vocabulary.category')}</th>
              <th>{t('vocabulary.aliases')}</th>
              {isAdmin && <th aria-label="actions" />}
            </tr>
          </thead>
          <tbody>
            {data?.items.map((item) => (
              <tr key={item.id}>
                <td>{item.term}</td>
                <td>{t(`categories.${item.category}`)}</td>
                <td className="muted">{item.aliases.join('، ') || t('common.none')}</td>
                {isAdmin && (
                  <td>
                    <button type="button" className="link" onClick={() => remove.mutate(item.id)}>
                      {t('vocabulary.remove')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
